/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

// @mui icons

import LocationOnIcon from "@mui/icons-material/LocationOn";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";
import ProfilesList from "layouts/pages/profile/profile-overview/components/ProfilesList";
import DefaultProjectCard from "examples/Cards/ProjectCards/DefaultProjectCard";

// Overview page components
import Header from "layouts/pages/profile/components/Header";
import PlatformSettings from "layouts/pages/profile/profile-overview/components/PlatformSettings";

// Data
import { useState, useEffect } from "react";
import axios from "axios";
import { API_HOHOLOK, HOHOLOK_UPLOADS } from "utils";
import { logoutUser } from "actions/AuthAction";

// Images

function Overview() {
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [colleagues, setColleagues] = useState([]);
  const [proyeks, setProyeks] = useState([]);
  const [status, setStatus] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const userStorage = localStorage.getItem("user");
    if (userStorage) {
      const user = JSON.parse(userStorage);
      setUser(user);
      setUserId(user.uid);
      setStatus(user.status);
    } else {
      logoutUser(navigate);
    }
  }, []);

  const fetchUserDetail = () => {
    let url = "";
    if (status === "admin") {
      url = `${API_HOHOLOK}users/admin-detail/${userId}`;
    } else {
      url = `${API_HOHOLOK}users/detail/${userId}`;
    }
    axios.get(url).then((res) => {
      const data = res.data;
      if (data) {
        setUserDetail(data);
        // Format colleagues data untuk kompatibel dengan ProfilesList
        console.log(data.colleagues);
        const formattedColleagues = (data.colleagues || []).map(
          (colleague) => ({
            image: `${HOHOLOK_UPLOADS}${colleague.profil}`, // Gunakan profil jika ada, default ke team1
            name: colleague.nama_user,
            description: colleague.email,
            action: {
              type: "internal",
              route: "/pages/profile/profile-overview",
              color: "info",
              label: "view profile",
            },
          })
        );
        setColleagues(formattedColleagues);
        setProyeks(status === "user" ? data.company_projects : data.projects);
      } else {
        setUserDetail(null);
        setColleagues([]);
        setProyeks([]);
      }
    });
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetail();
    }
  }, [userId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header
        nama={user && user.nama}
        status={user && user.status}
        profil={user && user.profil}
      >
        <MDBox mt={5} mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} xl={4}>
              <PlatformSettings />
            </Grid>
            <Grid item xs={12} md={6} xl={4} sx={{ display: "flex" }}>
              <Divider orientation="vertical" sx={{ ml: -2, mr: 1 }} />
              <ProfileInfoCard
                title="Informasi Profil"
                description="Sebagai pelaksana proyek yang berdedikasi profesional dan antikorupsi, saya berkomitmen untuk menjalankan setiap tugas dengan integritas, transparansi, dan tanggung jawab penuh demi tercapainya hasil terbaik."
                info={{
                  fullName: user && user.nama,
                  Perusahaan: user && user.nama_perusahaan,
                  email: user && user.email,

                  status: user && user.status,
                }}
                social={[]}
                action={{ route: "", tooltip: "Edit Profile" }}
                shadow={false}
              />
              <Divider orientation="vertical" sx={{ mx: 0 }} />
            </Grid>
            <Grid item xs={12} xl={4}>
              <ProfilesList
                title="Kolega"
                profiles={colleagues}
                shadow={false}
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox pt={2} px={2} lineHeight={1.25}>
          <MDTypography variant="h6" fontWeight="medium">
            Proyek
          </MDTypography>
          <MDBox mb={1}>
            <MDTypography variant="button" color="text">
              Daftar proyek yang{" "}
              {status === "user" ? "Perusahaan saya" : "Dinas saya"} ikuti
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={6}>
            {proyeks.map((proyek) => {
              // Format tanggal dengan singkatan bulan Indonesia
              const formatTanggalIndonesia = (dateString) => {
                const date = new Date(dateString);
                const bulan = [
                  "Jan",
                  "Feb",
                  "Mar",
                  "Apr",
                  "Mei",
                  "Jun",
                  "Jul",
                  "Ags",
                  "Sep",
                  "Okt",
                  "Nov",
                  "Des",
                ];
                const tanggal = date.getDate();
                const bulanIndex = date.getMonth();
                const tahun = date.getFullYear();
                return `${tanggal} ${bulan[bulanIndex]} ${tahun}`;
              };

              const tanggalMulai = formatTanggalIndonesia(proyek.tanggal_mulai);
              const tanggalSelesai = formatTanggalIndonesia(
                proyek.tanggal_selesai
              );

              // Format anggaran ke Rupiah
              const formatRupiah = (amount) => {
                return new Intl.NumberFormat("id-ID", {
                  style: "currency",
                  currency: "IDR",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(amount);
              };

              // Format authors dari data proyek - hanya tampilkan jika ada profil
              const authors = [];

              // Tambahkan kontraktor jika ada profil
              if (
                proyek.nama_user_kontraktor &&
                proyek.profil_user_kontraktor &&
                proyek.profil_user_kontraktor !== ""
              ) {
                authors.push({
                  image: `${HOHOLOK_UPLOADS}${proyek.profil_user_kontraktor}`,
                  name: `${proyek.nama_user_kontraktor} (Kontraktor)`,
                });
              }

              // Tambahkan konsultan perencana jika ada profil
              if (
                proyek.nama_user_perencana &&
                proyek.profil_user_perencana &&
                proyek.profil_user_perencana !== ""
              ) {
                authors.push({
                  image: `${HOHOLOK_UPLOADS}${proyek.profil_user_perencana}`,
                  name: `${proyek.nama_user_perencana} (Perencana)`,
                });
              }

              // Tambahkan konsultan pengawas jika ada profil
              if (
                proyek.nama_user_pengawas &&
                proyek.profil_user_pengawas &&
                proyek.profil_user_pengawas !== ""
              ) {
                authors.push({
                  image: `${HOHOLOK_UPLOADS}${proyek.profil_user_pengawas}`,
                  name: `${proyek.nama_user_pengawas} (Pengawas)`,
                });
              }

              return (
                <Grid item xs={12} md={6} xl={3} key={proyek.id_proyek}>
                  <DefaultProjectCard
                    image=""
                    label={proyek.nama_proyek}
                    title={`${tanggalMulai} - ${tanggalSelesai}`}
                    description={
                      <MDBox>
                        <MDTypography
                          variant="button"
                          fontWeight="light"
                          color="text"
                        >
                          Anggaran: {formatRupiah(proyek.total_anggaran)} |
                          Peran:{" "}
                          {status === "user"
                            ? proyek.peran_perusahaan
                            : "admin"}
                        </MDTypography>
                        <MDBox mt={1} display="flex" alignItems="center">
                          <LocationOnIcon
                            sx={{ fontSize: 16, color: "info.main", mr: 0.5 }}
                          />
                          <MDTypography
                            component={Link}
                            to={`/pages/proyek/map`}
                            variant="button"
                            fontWeight="light"
                            color="info"
                            sx={{
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {proyek.latitude}, {proyek.longitude}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    }
                    action={{
                      type: "internal",
                      route: "/pages/profile/profile-overview",
                      color: "info",
                      label: "view project",
                    }}
                    authors={authors}
                  />
                </Grid>
              );
            })}
          </Grid>
        </MDBox>
      </Header>
      <Footer />
    </DashboardLayout>
  );
}

export default Overview;
